import { createReference } from '@medplum/core';
import { Coding, Location, Practitioner, PractitionerRole, Reference, Schedule } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { parseSlotToOneTimeFormData } from '../Availability';
import { getBrowserTimezone } from '../utils/common';
import { alert } from '@/services/alert';
import dayjs from 'dayjs';

interface SlotData {
  practitioner: Practitioner;
  encounterClass: Coding;
  timezone: string;
  date: Date;
  startTime: string;
  endTime: string;
  appointmentTypes: Coding[];
  location: Location | null;
  schedulingGuideline?: string;
}

/**
 * Resolve the Practitioner backing a Slot via Schedule -> PractitionerRole -> Practitioner.
 * Used when the caller doesn't already know the practitioner (e.g. editing from a list of
 * many providers' slots).
 */
async function resolvePractitionerFromSlotSchedule(
  medplum: ReturnType<typeof useMedplum>,
  scheduleRef: Reference<Schedule> | undefined
): Promise<Practitioner | undefined> {
  if (!scheduleRef?.reference) {
    return undefined;
  }
  const schedule = await medplum.readReference(scheduleRef);
  const roleRef = schedule.actor?.find((a) => a.reference?.startsWith('PractitionerRole/')) as
    | Reference<PractitionerRole>
    | undefined;
  if (!roleRef?.reference) {
    return undefined;
  }
  const role = await medplum.readReference(roleRef);
  if (!role.practitioner?.reference) {
    return undefined;
  }
  return medplum.readReference(role.practitioner as Reference<Practitioner>);
}

export default function useSlotData(slotId: string | null, initialPractitioner: Practitioner | null) {
  const medplum = useMedplum();
  const browserTimezone = getBrowserTimezone();
  const [isLoadingSlot, setIsLoadingSlot] = useState(false);
  const [slotData, setSlotData] = useState<SlotData | undefined>(undefined);

  useEffect(() => {
    async function loadSlotData() {
      if (!slotId) {
        return;
      }
      setIsLoadingSlot(true);
      try {
        const slot = await medplum.readResource('Slot', slotId);
        if (cancelled) return;

        // Use the supplied practitioner when known; otherwise derive it from the slot's schedule.
        const practitioner = initialPractitioner ?? (await resolvePractitionerFromSlotSchedule(medplum, slot.schedule));
        if (cancelled) return;
        if (!practitioner) {
          throw new Error('Could not resolve the provider for this time block');
        }

        const practitionerRef = createReference(practitioner);
        const formData = parseSlotToOneTimeFormData(slot, practitionerRef, browserTimezone);
        setSlotData({
          practitioner,
          encounterClass: formData.encounterClass,
          timezone: formData.timezone,
          date: dayjs(formData.date).toDate(),
          startTime: formData.startTime,
          endTime: formData.endTime,
          appointmentTypes: formData.appointmentTypes,
          location: null,
          schedulingGuideline: formData.schedulingGuideline,
        });
        if (formData.location) {
          const loc = await medplum.readReference(formData.location);
          if (cancelled) return;
          setSlotData((prev) => {
            return {
              ...(prev as SlotData),
              location: loc as Location,
            };
          });
        }
      } catch (error) {
        if (!cancelled) {
          alert.error(`Couldn't load availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) setIsLoadingSlot(false);
      }
    }

    let cancelled = false;
    loadSlotData();

    return () => {
      cancelled = true;
    };
  }, [slotId, initialPractitioner, medplum, browserTimezone]);

  return {
    isLoadingSlot,
    slotData,
  };
}
